/**
 * Reports when an element has been on screen at least once.
 *
 * The wall has no pane limit any more — what bounds it is whether a tile is still
 * big enough to read, not a count — so an operator can put more sessions on it
 * than fit. Every mounted terminal costs a WebSocket, a scrollback replay of up
 * to a mebibyte, and a `GET /sessions/{id}` every ten seconds; opening twenty of
 * those because twenty tiles exist, when three are on screen, is a bill nobody
 * asked for.
 *
 * **Once**, and it does not go back. A pane that unmounted on scrolling away
 * would reconnect and replay on the way back, and for an alternate-screen agent
 * CLI that means a full redraw every time the wall moves — the cost this exists
 * to avoid, paid repeatedly instead of once. So this gates the *first* attach and
 * then stops caring: the bill follows what the operator has actually looked at.
 *
 * Its own hook rather than an effect in the tile, because the tile must render
 * the placeholder and the terminal from the same branch, and a hook is the only
 * way to know which before deciding.
 */

import { useEffect, useRef, useState } from 'react'

export function useOnceVisible<T extends HTMLElement>(): {
  ref: (node: T | null) => void
  seen: boolean
} {
  const [seen, setSeen] = useState(false)
  const node = useRef<T | null>(null)
  const observer = useRef<IntersectionObserver | null>(null)

  // A callback ref, not a returned object ref: the tile hands this to a element
  // that only exists in one of two branches, so it has to be told when the node
  // arrives rather than reading it after a render that may not have made one.
  const ref = (next: T | null) => {
    node.current = next
    if (seen || !next) return
    observer.current?.disconnect()
    // No IntersectionObserver — jsdom in a unit test, an ancient browser — means
    // attach now. Degrading to "never visible" would leave a wall of placeholders
    // and no terminals at all, which is worse than paying for every pane.
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true)
      return
    }
    // A margin, so a tile just past the edge is already connected by the time it
    // is scrolled to: attaching at the exact boundary means the operator watches
    // it connect and replay, which reads as the wall being slow.
    observer.current = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return
        observer.current?.disconnect()
        observer.current = null
        setSeen(true)
      },
      { rootMargin: '200px' },
    )
    observer.current.observe(next)
  }

  useEffect(() => () => observer.current?.disconnect(), [])

  return { ref, seen }
}
